import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheckIcon, ServerIcon, RefreshCwIcon, LockIcon, UsersIcon, GraduationCapIcon, LifeBuoyIcon, CheckIcon, ArrowRightIcon, ArrowLeftIcon } from 'lucide-react';
import { Contact } from '../components/Contact';
import { Link } from 'react-router-dom';
import { NexyomLogo } from '../components/NexyomLogo';
const steps = [{
  icon: ShieldCheckIcon,
  title: '1. Audit & Diagnostic de Sécurité',
  description: 'Pas de blabla. On identifie les vulnérabilités qui mettent votre entreprise en danger.',
  details: ["Analyse complète de l'infrastructure (messagerie, DNS, points d'accès)", 'Vérification des volumes et comptes orphelins', 'Détection des risques critiques : Absence MFA, Shadow IT, protocoles non sécurisés', 'Rapport clair + plan de sécurisation et migration'],
  highlight: "Vous découvrez exactement comment un attaquant peut entrer aujourd'hui — et comment Nexyom neutralise ces failles."
}, {
  icon: ServerIcon,
  title: '2. Création & Configuration Tenant',
  description: 'Un tenant 365 mal configuré = une porte ouverte aux attaques. Nous le construisons selon les standards cybersécurité.',
  details: ['Création tenant (France / UE)', 'Sécurisation DNS : SPF, DKIM, DMARC strict', 'Architecture Zero Trust avec MFA obligatoire', 'Blocage des protocoles vulnérables et protection anti-phishing'],
  highlight: "Vous passez d'un environnement vulnérable à une infrastructure capable de bloquer automatiquement les attaques."
}, {
  icon: RefreshCwIcon,
  title: '3. Migration Sans Interruption',
  description: "Notre processus garantit une migration sans perte et sans fenêtre d'exploitation pour un attaquant.",
  details: ['Migration IMAP/POP/Exchange → Microsoft 365', 'Transfert sécurisé contacts, calendriers, archives', 'Préservation totale des historiques', 'Validation utilisateur + bascule contrôlée'],
  highlight: 'Vos données migrent de façon chiffrée, sans downtime, et sans laisser de brèches dans votre messagerie.'
}, {
  icon: LockIcon,
  title: '4. Sécurité Avancée & Compliance',
  description: 'Transformation du tenant en véritable système de défense proactif.',
  details: ['Microsoft Defender for Business', 'EDR Windows & Mac (détection temps réel)', "Politiques d'accès Zero Trust & Intune", 'Tableau de bord Nexyom : alertes et menaces détectées'],
  highlight: 'Votre entreprise détecte et bloque les attaques avant même que vos collaborateurs ne les voient.'
}, {
  icon: UsersIcon,
  title: '5. Gouvernance & Rôles',
  description: 'La sécurité passe aussi par le contrôle strict des accès.',
  details: ['Création Admin Global (client)', 'Rôle Admin Nexyom à privilège limité', 'Documentation claire des accès', 'Processus de gestion des employés sortants'],
  highlight: 'Réduction immédiate des risques internes et erreurs de gestion de comptes.'
}, {
  icon: GraduationCapIcon,
  title: '6. Configuration & Formation',
  description: 'Former les équipes = réduire 80% des risques humains.',
  details: ['Installation et configuration sécurisée Outlook (PC/Mac/Mobile)', 'Formation visio (45 min) : détection phishing', 'Bonnes pratiques mots de passe', 'Utilisation Teams, OneDrive, SharePoint'],
  highlight: "Vos collaborateurs deviennent un bouclier plutôt qu'une faille."
}, {
  icon: LifeBuoyIcon,
  title: '7. Support & Surveillance',
  description: 'Un service pensé comme un SOC simplifié pour PME.',
  details: ['Monitoring permanent 24/7', 'Alertes de sécurité en temps réel', 'Gestion licences & accès', 'Optimisation continue de la sécurité'],
  highlight: 'Votre messagerie reste sécurisée 24/7 — pas seulement le jour de la migration.'
}];
const pricing = [{
  title: 'Pack Migration Sécurisée',
  price: '890€',
  suffix: 'HT',
  details: "Inclut jusqu'à 5 utilisateurs",
  extra: '+80€ HT / utilisateur supp.',
  features: ['Audit complet', 'Configuration Zero Trust', 'Migration sans coupure', 'Formation utilisateurs'],
  highlight: false
}, {
  title: 'Pack Sécurité',
  price: '390€',
  suffix: 'HT / mois',
  details: 'Protection continue',
  extra: 'Engagement annuel',
  features: ['Microsoft Defender', 'Monitoring 24/7', 'Alertes sécurité', 'Hardening continu'],
  highlight: true
}, {
  title: 'Pack Support',
  price: '190€',
  suffix: 'HT / mois',
  details: "Jusqu'à 20 utilisateurs",
  extra: 'Support réactif',
  features: ['Gestion des licences', 'Support technique', 'Administration tenant', 'Réponse incidents'],
  highlight: false
}];
export function Microsoft365Page() {
  return <div className="w-full bg-white" data-id="element-163">
      {/* Hero Section */}
      <section className="relative min-h-[80vh] flex items-center justify-center bg-black text-white overflow-hidden pt-20" data-id="element-164">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.03)_1px,transparent_1px)] bg-[size:80px_80px]" data-id="element-165" />

        {/* Navigation Back */}
        <Link to="/" className="absolute top-8 left-8 z-20 flex items-center gap-2 text-gray-400 hover:text-white transition-colors uppercase tracking-wider text-sm font-medium group" data-id="element-166">
          <ArrowLeftIcon className="w-4 h-4 transition-transform group-hover:-translate-x-1" data-id="element-167" />
          Retour à l'accueil
        </Link>

        {/* Logo in top right */}
        <div className="absolute top-8 right-8 z-20" data-id="element-168">
          <NexyomLogo variant="icon" colorScheme="dark" size="sm" data-id="element-169" />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 py-20 text-center" data-id="element-170">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-171">
            <div className="inline-block px-4 py-2 bg-blue-500/10 border border-blue-500/20 text-blue-400 mb-8 font-medium tracking-widest uppercase text-sm" data-id="element-172">
              Service Premium
            </div>
            <h1 className="text-5xl md:text-7xl font-black tracking-tighter mb-8 leading-[1.1]" data-id="element-173">
              MIGRATION & SÉCURISATION
              <br data-id="element-174" />
              <span className="text-blue-500" data-id="element-175">MICROSOFT 365</span>
            </h1>
          </motion.div>

          <motion.p initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.15,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-xl md:text-2xl text-gray-400 mb-14 max-w-3xl mx-auto font-light leading-relaxed" data-id="element-176">
            Protégez votre entreprise contre le phishing, les usurpations
            d'identité et les fuites de données — tout en modernisant votre
            messagerie.
          </motion.p>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.3,
          ease: [0.16, 1, 0.3, 1]
        }} data-id="element-177">
            <a href="#contact" className="group inline-flex items-center gap-3 bg-blue-500 text-white px-10 py-5 text-lg font-semibold transition-all duration-300 hover:bg-blue-600 relative overflow-hidden" data-id="element-178">
              <span className="relative z-10" data-id="element-179">Sécuriser mon entreprise</span>
              <ArrowRightIcon className="w-5 h-5 relative z-10 transition-transform duration-300 group-hover:translate-x-1" data-id="element-180" />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-181" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Process Steps */}
      <section className="py-40 bg-white relative" data-id="element-182">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent" data-id="element-183" />

        <div className="max-w-6xl mx-auto px-6" data-id="element-184">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-center mb-32" data-id="element-185">
            <h2 className="text-5xl md:text-6xl font-black text-black mb-6 tracking-tight" data-id="element-186">
              Notre Méthodologie
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto font-light" data-id="element-187">
              Une approche rigoureuse pour transformer votre vulnérabilité en
              forteresse.
            </p>
          </motion.div>

          <div className="space-y-24" data-id="element-188">
            {steps.map((step, index) => {
            const Icon = step.icon;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 40
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true,
              margin: '-100px'
            }} transition={{
              duration: 0.8,
              ease: [0.16, 1, 0.3, 1]
            }} className="grid md:grid-cols-[1fr_2fr] gap-12 items-start group" data-id="element-189">
                  <div className="bg-gray-50 p-10 border border-gray-100 transition-all duration-500 group-hover:border-blue-500/30 group-hover:shadow-xl group-hover:shadow-blue-500/5" data-id="element-190">
                    <div className="w-16 h-16 bg-blue-500 flex items-center justify-center mb-6" data-id="element-191">
                      <Icon className="w-8 h-8 text-white" strokeWidth={1.5} data-id="element-192" />
                    </div>
                    <h3 className="text-2xl font-bold text-black tracking-tight leading-tight" data-id="element-193">
                      {step.title}
                    </h3>
                  </div>

                  <div className="pt-4" data-id="element-194">
                    <p className="text-xl text-gray-900 font-medium mb-8 leading-relaxed" data-id="element-195">
                      {step.description}
                    </p>

                    <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-4 mb-10" data-id="element-196">
                      {step.details.map((detail, i) => <li key={i} className="flex items-start gap-3 text-gray-600 font-light" data-id="element-197">
                          <span className="w-1.5 h-1.5 bg-blue-500 mt-2.5 flex-shrink-0" data-id="element-198" />
                          <span data-id="element-199">{detail}</span>
                        </li>)}
                    </ul>

                    <div className="bg-black text-white p-6 border-l-4 border-blue-500" data-id="element-200">
                      <p className="font-medium italic" data-id="element-201">"{step.highlight}"</p>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-40 bg-gray-50 relative" data-id="element-202">
        <div className="max-w-7xl mx-auto px-6" data-id="element-203">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-center mb-24" data-id="element-204">
            <h2 className="text-5xl md:text-6xl font-black text-black mb-6 tracking-tight" data-id="element-205">
              Tarification Transparente
            </h2>
            <p className="text-xl text-gray-600 font-light" data-id="element-206">
              Des offres scalables adaptées à votre structure
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 items-start" data-id="element-207">
            {pricing.map((plan, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 0.8,
            delay: index * 0.1,
            ease: [0.16, 1, 0.3, 1]
          }} className={`relative p-10 bg-white border transition-all duration-500 hover:shadow-2xl ${plan.highlight ? 'border-blue-500 shadow-xl scale-105 z-10' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-208">
                {plan.highlight && <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-blue-500 text-white px-4 py-1 text-sm font-bold tracking-widest uppercase" data-id="element-209">
                    Recommandé
                  </div>}

                <h3 className="text-2xl font-bold text-black mb-4" data-id="element-210">
                  {plan.title}
                </h3>
                <div className="flex items-baseline gap-1 mb-2" data-id="element-211">
                  <span className="text-4xl font-black text-black" data-id="element-212">
                    {plan.price}
                  </span>
                  <span className="text-gray-500 font-medium" data-id="element-213">
                    {plan.suffix}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mb-8 font-medium uppercase tracking-wide" data-id="element-214">
                  {plan.details}
                </p>

                <ul className="space-y-4 mb-10" data-id="element-215">
                  {plan.features.map((feature, i) => <li key={i} className="flex items-center gap-3 text-gray-700" data-id="element-216">
                      <CheckIcon className="w-5 h-5 text-blue-500" data-id="element-217" />
                      <span className="font-light" data-id="element-218">{feature}</span>
                    </li>)}
                </ul>

                <div className="pt-8 border-t border-gray-100 text-center" data-id="element-219">
                  <p className="text-sm text-gray-400 font-medium" data-id="element-220">
                    {plan.extra}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Target Audience */}
      <section className="py-32 bg-black text-white border-b border-white/10" data-id="element-221">
        <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-222">
          <h2 className="text-4xl font-bold mb-12 tracking-tight" data-id="element-223">
            Idéal pour
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-224">
            {['PME 5-100 employés', "Cabinets d'avocats", 'Secteur Médical', 'Industrie & Logistique'].map((target, i) => <div key={i} className="p-6 border border-white/10 bg-white/5 backdrop-blur-sm" data-id="element-225">
                <p className="font-medium text-gray-300" data-id="element-226">{target}</p>
              </div>)}
          </div>
        </div>
      </section>

      <Contact data-id="element-227" />

      <footer className="bg-black text-white py-16 border-t border-white/10" data-id="element-228">
        <div className="max-w-6xl mx-auto px-6" data-id="element-229">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6" data-id="element-230">
            <NexyomLogo variant="full" colorScheme="dark" size="sm" data-id="element-231" />
            <p className="text-gray-500 font-light tracking-wide" data-id="element-232">
              © 2025 Nexyom. Performance. Croissance. Ingénierie.
            </p>
          </div>
        </div>
      </footer>
    </div>;
}